/**
 * Mini-cart loyalty (Rivo): auth, points/claim rules, affordability, redeem CTAs.
 * Event delegation survives cart section morphs. Option drawer (section render) is mobile-only for multi-variant rewards.
 * Loyalty stays hidden until `document` dispatches `rivo-js-loaded`. Readiness is the flag set in that handler (no sync `window.RivoJS` bootstrap). `reinitializeLoyaltyCart` runs on `section:morph` (after `morphSection`) so loyalty refreshes when the cart section is morphed, including when the loyalty block is injected again. Not on `cart:update`, which often fires before morph.
 * The loyalty root in Liquid uses `data-skip-node-update` so morph does not re-apply server `hidden` over client-controlled visibility after cart line changes.
 *
 * RivoJS response shapes: https://developer-docs.rivo.io/docs/functions
 * - getCustomerDetails → { success, customer: { points_tally, … } }
 * - getCustomerPointsPurchases(opts, page) → { success, points_purchases: [{ points_amount, used_at, reward: { … } }] }
 * - redeemReward(rewardId) → Promise resolving to an object with nested `reward` (or `points_purchase.reward`).
 *   We read reward.product_id, variant_ids (array) / shopify_variant_id / variant_id via {@link pickVariantIdFromReward}; discount for redeem flows comes from the matching `points_purchase` row (see {@link pickCodeFromRedeemResByProdId} and {@link pickDiscountCodeFromPurchase}).
 *   Confirm field names against a live Rivo payload if redemption fails to parse.
 */

import { CartAddEvent, DiscountUpdateEvent, ThemeEvents } from "@theme/events";
import { morphSection } from "@theme/section-renderer";
import { fetchConfig } from "@theme/utilities";

const MIN_PTS = 99;
const RIVO_LOAD_EV = "rivo-js-loaded";

class LoyaltyCart {
  constructor() {
    /** @type {WeakMap<HTMLElement, number>} */
    this.latestRefreshSeqByRoot = new WeakMap();
    this.refreshSeq = 0;
    this.sectionMorphHooked = false;
    this.clickHooked = false;
    /** True after `document` dispatches `rivo-js-loaded` (set in the handler registered from {@link initialize}). */
    this.rivoLoadReceived = false;
  }

  /**
   * @param {HTMLElement} root
   * @param {number} mySeq
   * @returns {boolean}
   */
  loyaltyRefreshIsLatest(root, mySeq) {
    return this.latestRefreshSeqByRoot.get(root) === mySeq;
  }

  /**
   * @param {unknown} val
   * @returns {string}
   */
  toStr(val) {
    return val == null ? "" : String(val);
  }

  /**
   * @param {unknown} gidOrId
   * @returns {string}
   */
  normalizeRewardId(gidOrId) {
    const str = this.toStr(gidOrId);
    if (!str) return "";
    const parts = str.split("/");
    return parts[parts.length - 1] || str;
  }

  /**
   * @param {unknown} gidOrId
   * @returns {string}
   */
  normProdId(gidOrId) {
    const str = this.toStr(gidOrId);
    if (!str) return "";
    const tail = (str.split("/").pop() || str).split("?")[0];
    return tail || "";
  }

  /**
   * Whether Rivo has signaled readiness via `rivo-js-loaded` (not a probe of `window.RivoJS`).
   * Call sites that invoke Rivo still verify `window.RivoJS` methods exist.
   * @returns {boolean}
   */
  isRivoReady() {
    return this.rivoLoadReceived;
  }

  /**
   * @returns {Promise<Record<string, unknown> | null>}
   */
  async loadCustDetails() {
    if (!this.isRivoReady() || typeof window.RivoJS?.getCustomerDetails !== "function") {
      return null;
    }
    try {
      const res = await window.RivoJS.getCustomerDetails();
      return res && typeof res === "object" ? /** @type {Record<string, unknown>} */ (res) : null;
    } catch (error) {
      console.error("[Loyalty Cart] Failed to load customer details", error);
      return null;
    }
  }

  /**
   * @returns {Promise<Record<string, unknown> | null>}
   */
  async loadPtsPurchases() {
    if (!this.isRivoReady() || typeof window.RivoJS?.getCustomerPointsPurchases !== "function") {
      return null;
    }
    try {
      const res = await window.RivoJS.getCustomerPointsPurchases({ used: false });
      return res && typeof res === "object" ? /** @type {Record<string, unknown>} */ (res) : null;
    } catch (error) {
      console.error("[Loyalty Cart] Failed to load points purchases", error);
      return null;
    }
  }

  /**
   * Points balance from getCustomerDetails (nested under customer per Rivo docs).
   * @param {Record<string, unknown> | null} res
   * @returns {number}
   */
  customerPointsTally(res) {
    if (!res || res.success === false) {
      return NaN;
    }
    const cust = res.customer;
    if (cust && typeof cust === "object" && "points_tally" in cust) {
      const num = Number(/** @type {Record<string, unknown>} */ (cust).points_tally);
      return Number.isFinite(num) ? num : NaN;
    }
    if ("points_tally" in res) {
      const num = Number(res.points_tally);
      return Number.isFinite(num) ? num : NaN;
    }
    return NaN;
  }

  /**
   * @param {number} pointsTally
   * @returns {string}
   */
  pointsLineMsg(pointsTally) {
    return Number.isFinite(pointsTally) ? `You have ${Math.round(pointsTally)} pts` : "";
  }

  /**
   * Shares the same points-line message across all loyalty points nodes, regardless of root.
   * @param {number} pointsTally
   */
  syncPointsTally(pointsTally) {
    document.querySelectorAll("[data-loyalty-points-line]").forEach(el => {
      if (!(el instanceof HTMLElement)) return;
      el.textContent = this.pointsLineMsg(pointsTally);
    });
  }

  /**
   * @param {Record<string, unknown> | null} res
   * @returns {Record<string, unknown>[]}
   */
  pointsPurchasesList(res) {
    if (!res || res.success === false || !Array.isArray(res.points_purchases)) {
      return [];
    }
    return /** @type {Record<string, unknown>[]} */ (res.points_purchases);
  }

  /**
   * @param {Record<string, unknown>} purchase
   * @returns {number}
   */
  purchasePtsCost(purchase) {
    const raw = purchase.points_amount ?? purchase.points_cost ?? purchase.points ?? purchase.point_cost;
    const num = Number(raw);
    return Number.isFinite(num) ? num : 0;
  }

  /**
   * @param {Record<string, unknown>} purchase
   * @returns {string}
   */
  purchaseProdId(purchase) {
    const prodNest = /** @type {Record<string, unknown> | undefined} */ (
      typeof purchase.product === "object" && purchase.product != null ? purchase.product : undefined
    );
    const rwNest = /** @type {Record<string, unknown> | undefined} */ (
      typeof purchase.reward === "object" && purchase.reward != null ? purchase.reward : undefined
    );
    return this.normProdId(
      purchase.product_id ??
        purchase.shopify_product_id ??
        prodNest?.id ??
        purchase.reward_product_id ??
        rwNest?.shopify_product_id ??
        rwNest?.product_id
    );
  }

  /**
   * @param {Record<string, unknown>} purchase
   * @returns {string}
   */
  purchaseRedeemId(purchase) {
    const rw =
      purchase.reward && typeof purchase.reward === "object"
        ? /** @type {Record<string, unknown>} */ (purchase.reward)
        : null;
    const raw = rw?.id ?? purchase.reward_id ?? purchase.points_reward_id;
    return this.normalizeRewardId(raw);
  }

  /**
   * @param {Record<string, unknown>[]} unusedPurchases
   * @param {string} prodIdNorm
   * @param {string} purchRwIdNorm
   * @returns {Record<string, unknown> | null}
   */
  findClaimedPurchase(unusedPurchases, prodIdNorm, purchRwIdNorm) {
    const found = unusedPurchases.find(purchase => {
      return (
        this.purchaseProdId(purchase) === prodIdNorm &&
        this.normalizeRewardId(this.purchaseRedeemId(purchase)) === purchRwIdNorm
      );
    });
    return found && typeof found === "object" ? found : null;
  }

  /**
   * @param {unknown} res
   * @returns {Record<string, unknown> | null}
   */
  extractRewardFromRedeemRes(res) {
    if (!res || typeof res !== "object") return null;
    const obj = /** @type {Record<string, unknown>} */ (res);
    if (obj.success === false) return null;
    const reward = obj.reward;
    if (reward && typeof reward === "object") return /** @type {Record<string, unknown>} */ (reward);
    const pointsPur = obj.points_purchase;
    if (pointsPur && typeof pointsPur === "object") {
      const nestedReward = /** @type {Record<string, unknown>} */ (pointsPur).reward;
      if (nestedReward && typeof nestedReward === "object")
        return /** @type {Record<string, unknown>} */ (nestedReward);
    }
    return null;
  }

  /**
   * @param {Record<string, unknown> | null} reward
   * @returns {string}
   */
  rewardProductIdFromReward(reward) {
    if (!reward || typeof reward !== "object") return "";
    return this.normProdId(reward.product_id ?? reward.shopify_product_id ?? reward.shopifyProductId);
  }

  /**
   * @param {unknown} rawVid
   * @returns {string}
   */
  normVariantIdForCart(rawVid) {
    const str = this.toStr(rawVid).trim();
    if (!str) return "";
    return this.normProdId(str) || str;
  }

  /**
   * Rivo catalog rewards often expose `variant_ids` (array); see .cursor/references/rivo-loyalty-redemption.md.
   * Discount code for redeem flows comes from {@link pickCodeFromRedeemResByProdId}.
   * @param {Record<string, unknown> | null} reward
   * @returns {string}
   */
  pickVariantIdFromReward(reward) {
    if (!reward || typeof reward !== "object") return "";
    const vNest =
      reward.variant && typeof reward.variant === "object"
        ? /** @type {Record<string, unknown>} */ (reward.variant)
        : null;
    let variantId = reward.shopify_variant_id ?? reward.variant_id ?? reward.shopifyVariantId ?? vNest?.id;
    if (variantId == null || this.toStr(variantId).trim() === "") {
      const variantIds = reward.variant_ids;
      if (Array.isArray(variantIds) && variantIds.length > 0) {
        variantId = variantIds[0];
      } else if (typeof variantIds === "string") {
        const str = variantIds.trim();
        if (str.startsWith("[")) {
          try {
            const parsed = JSON.parse(str);
            if (Array.isArray(parsed) && parsed.length > 0) variantId = parsed[0];
          } catch (error) {
            console.error("[Loyalty Cart] Failed parsing reward variant IDs", error);
          }
        }
        if (variantId == null || this.toStr(variantId).trim() === "") {
          const first = str.split(/[,\s]+/).find(Boolean);
          if (first) variantId = first;
        }
      }
    }
    return this.normVariantIdForCart(variantId);
  }

  /**
   * `redeemReward` may return `points_purchases` or a single `points_purchase`. Code often lives on the purchase row, not on `reward`.
   * @param {Record<string, unknown> | null} rewardRes
   * @returns {Record<string, unknown>[]}
   */
  pointsPurchasesFromRedeemRes(rewardRes) {
    if (!rewardRes || typeof rewardRes !== "object") return [];
    if (Array.isArray(rewardRes.points_purchases)) {
      return /** @type {Record<string, unknown>[]} */ (
        rewardRes.points_purchases.filter(purchase => purchase && typeof purchase === "object")
      );
    }
    const single = rewardRes.points_purchase;
    if (single && typeof single === "object") {
      return [/** @type {Record<string, unknown>} */ (single)];
    }
    return [];
  }

  /**
   * @param {Record<string, unknown> | null} rewardRes
   * @param {string} prodIdNorm
   * @returns {string}
   */
  pickCodeFromRedeemResByProdId(rewardRes, prodIdNorm) {
    const wanted = this.normProdId(prodIdNorm);
    if (!wanted) return "";
    const matchPur = this.pointsPurchasesFromRedeemRes(rewardRes).find(purchase => {
      return this.purchaseProdId(purchase) === wanted;
    });
    return matchPur ? this.pickDiscountCodeFromPurchase(matchPur) : "";
  }

  /**
   * Discount code is often on the points_purchase root, not on reward (runtime log: reward had no code* keys).
   * @param {Record<string, unknown> | null} purchase
   * @returns {string}
   */
  pickDiscountCodeFromPurchase(purchase) {
    if (!purchase || typeof purchase !== "object") return "";
    const direct =
      purchase.discount_code ??
      purchase.code ??
      purchase.redemption_code ??
      purchase.coupon_code ??
      purchase.redeem_code ??
      purchase.shopify_discount_code ??
      purchase.discount_coupon_code;
    let out = this.toStr(direct).trim();
    if (out) return out;
    const meta = purchase.metadata;
    if (meta && typeof meta === "object") {
      const metaRec = /** @type {Record<string, unknown>} */ (meta);
      out = this.toStr(metaRec.discount_code ?? metaRec.code ?? metaRec.redemption_code).trim();
    }
    return out;
  }

  /**
   * @returns {string[]}
   */
  collectCartSectionIds() {
    /** @type {string[]} */
    const ids = [];
    document.querySelectorAll("cart-items-component").forEach(el => {
      if (el instanceof HTMLElement && el.dataset.sectionId && !ids.includes(el.dataset.sectionId)) {
        ids.push(el.dataset.sectionId);
      }
    });
    const discountNode = document.querySelector("cart-discount-component");
    if (
      discountNode instanceof HTMLElement &&
      discountNode.dataset.sectionId &&
      !ids.includes(discountNode.dataset.sectionId)
    ) {
      ids.push(discountNode.dataset.sectionId);
    }
    return ids;
  }

  /**
   * @param {HTMLElement} drawerHost
   * @returns {string}
   */
  readDrawerVariantId(drawerHost) {
    const input = drawerHost.querySelector("[data-variant-id-input]");
    if (!(input instanceof HTMLInputElement)) return "";
    return this.toStr(input.value).trim();
  }

  /**
   * @returns {Promise<string[]>}
   */
  async fetchCartDiscountCodes() {
    const rootPath = window.Shopify?.routes?.root || "/";
    const res = await fetch(`${rootPath}cart.js`);
    if (!res.ok) return [];
    const cartJson = await res.json();
    if (!Array.isArray(cartJson.discount_codes)) return [];
    const discountCodes = /** @type {unknown[]} */ (cartJson.discount_codes);
    return discountCodes
      .filter(
        (/** @type {unknown} */ discountCode) =>
          discountCode && typeof discountCode === "object" && "code" in discountCode
      )
      .map((/** @type {unknown} */ discountCode) =>
        this.toStr(/** @type {Record<string, unknown>} */ (discountCode).code)
      );
  }

  /**
   * Applies loyalty discount on the cart first (`POST cart/update.js`), then adds the variant (`POST cart/add.js`).
   * @param {string | number} variantIdRaw
   * @param {string} discountCode
   * @returns {Promise<Record<string, unknown>>}
   */
  async loyaltyAddVariantWithDiscount(variantIdRaw, discountCode) {
    const variantId = Number(this.toStr(variantIdRaw).trim());
    if (!Number.isFinite(variantId) || variantId < 1) {
      throw new Error("Invalid variant");
    }

    const rootPath = window.Shopify?.routes?.root || "/";
    const themeRoutes = window.Theme?.routes;
    const cartAddUrl = themeRoutes?.cart_add_url || `${rootPath}cart/add.js`;
    const cartUpdateUrl = themeRoutes?.cart_update_url || `${rootPath}cart/update.js`;

    /** @type {{ items: Array<{ id: number; quantity: number }>; sections?: string; sections_url?: string }} */
    const payload = { items: [{ id: variantId, quantity: 1 }] };

    try {
      const utils = /** @type {any} */ (window).GwpCartUtils;
      if (utils?.getItemsToBundle && payload.items) {
        const gwpItems = /** @type {Array<{ id: number; quantity: number }>} */ (utils.getItemsToBundle(0));
        gwpItems.forEach((/** @type {{ id: number; quantity: number }} */ giftItem) => {
          if (!payload.items.some(payloadItem => payloadItem.id === giftItem.id)) {
            payload.items.push(giftItem);
          }
        });
      }
    } catch (error) {
      console.error("[Loyalty Cart] Failed to bundle GWP items", error);
    }

    const sectionIds = this.collectCartSectionIds();
    if (sectionIds.length > 0) {
      payload.sections = sectionIds.join(",");
      payload.sections_url = window.location.pathname;
    }

    const code = this.toStr(discountCode).trim();
    if (code) {
      const existing = await this.fetchCartDiscountCodes();
      if (!existing.some(existingCode => existingCode.toUpperCase() === code.toUpperCase())) {
        const merged = [...existing, code].join(",");
        /** @type {Record<string, unknown>} */
        const updateBody = { discount: merged };
        if (sectionIds.length > 0) updateBody.sections = sectionIds;

        const updateCfg = fetchConfig("json", { body: JSON.stringify(updateBody) });
        const updRes = await fetch(cartUpdateUrl, updateCfg);
        if (!updRes.ok) {
          const errData = await updRes.json().catch(() => ({}));
          throw new Error(/** @type {any} */ (errData).description || "Failed to apply discount");
        }
        const updData = await updRes.json();
        const updSections = updData.sections || {};
        for (const sectionId of sectionIds) {
          if (updSections[sectionId]) await morphSection(sectionId, updSections[sectionId]);
        }
        document.dispatchEvent(new DiscountUpdateEvent(updData, "loyalty-cart"));
      }
    }

    document.dispatchEvent(new CustomEvent("cart:add-start"));

    const addRes = await fetch(cartAddUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!addRes.ok) {
      const errData = await addRes.json().catch(() => ({}));
      throw new Error(/** @type {any} */ (errData).description || "Failed to add to cart");
    }

    const cartData = await addRes.json();
    const sections = cartData.sections || {};
    for (const sectionId of sectionIds) {
      if (sections[sectionId]) await morphSection(sectionId, sections[sectionId]);
    }

    document.dispatchEvent(
      new CartAddEvent(cartData, "loyalty-cart", {
        source: "loyalty",
        itemCount: cartData.item_count,
        variantId: String(variantId),
        sections,
      })
    );

    return cartData;
  }

  /**
   * Keeps the add icon in the DOM; only label text and Tailwind `hidden` on the labels.
   * @param {HTMLElement} redeemBtn
   * @param {boolean} showAddCopy
   */
  syncRedeemBtnLabels(redeemBtn, showAddCopy) {
    const ctaLbl = redeemBtn.querySelector("[data-loyalty-redeem-cta-label]");
    const addLbl = redeemBtn.querySelector("[data-loyalty-redeem-add-label]");
    if (ctaLbl instanceof HTMLElement && addLbl instanceof HTMLElement) {
      ctaLbl.classList.toggle("hidden", showAddCopy);
      addLbl.classList.toggle("hidden", !showAddCopy);
      ctaLbl.style.removeProperty("display");
      addLbl.style.removeProperty("display");
    }
  }

  /**
   * Busy: spinner + hide icon and labels. Idle: hide spinner + show icon (labels from refreshLoyalty / syncRedeemBtnLabels).
   * @param {HTMLElement} btn
   * @param {boolean} busy
   */
  setCardRedeemBusy(btn, busy) {
    const spinner = btn.querySelector("[data-loyalty-redeem-spinner]");
    const icon = btn.querySelector(".loyaltyCart__redeemIcon");
    const ctaLbl = btn.querySelector("[data-loyalty-redeem-cta-label]");
    const addLbl = btn.querySelector("[data-loyalty-redeem-add-label]");
    btn.setAttribute("aria-busy", busy ? "true" : "false");
    if (spinner instanceof HTMLElement) spinner.classList.toggle("hidden", !busy);
    if (icon instanceof HTMLElement) icon.classList.toggle("hidden", busy);
    if (busy) {
      if (ctaLbl instanceof HTMLElement) ctaLbl.classList.add("hidden");
      if (addLbl instanceof HTMLElement) addLbl.classList.add("hidden");
    }
  }

  /**
   * @param {HTMLElement} btn
   * @param {boolean} busy
   */
  setDrawerRedeemBusy(btn, busy) {
    const spinner = btn.querySelector("[data-loyalty-drawer-redeem-spinner]");
    const label = btn.querySelector("[data-loyalty-drawer-redeem-label]");
    btn.setAttribute("aria-busy", busy ? "true" : "false");
    if (spinner instanceof HTMLElement) spinner.classList.toggle("hidden", !busy);
    if (label instanceof HTMLElement) label.classList.toggle("hidden", busy);
  }

  /**
   * @param {HTMLElement} root
   * @param {boolean} isLoading
   */
  setRootLoading(root, isLoading) {
    const loadingNode = root.querySelector("[data-loyalty-loading]");
    const track = root.querySelector("[data-loyalty-track]");
    root.setAttribute("aria-busy", isLoading ? "true" : "false");
    if (loadingNode instanceof HTMLElement) {
      loadingNode.hidden = !isLoading;
    }
    if (track instanceof HTMLElement) {
      track.hidden = isLoading;
    }
    if (isLoading) {
      root.hidden = false;
    }
  }

  /**
   * @param {HTMLElement} root
   * @returns {Promise<void>}
   */
  async refreshLoyalty(root) {
    const mySeq = ++this.refreshSeq;
    this.latestRefreshSeqByRoot.set(root, mySeq);
    this.setRootLoading(root, true);

    const track = root.querySelector("[data-loyalty-track]");
    const cards = root.querySelectorAll("[data-loyalty-card]");

    if (!this.isRivoReady()) {
      return;
    }

    if (typeof window.RivoJS === "undefined" || window.RivoJS == null) {
      if (this.loyaltyRefreshIsLatest(root, mySeq)) {
        this.setRootLoading(root, false);
        root.hidden = true;
      }
      return;
    }

    if (root.dataset.customerAuth !== "true") {
      if (this.loyaltyRefreshIsLatest(root, mySeq)) {
        this.setRootLoading(root, false);
        root.hidden = true;
      }
      return;
    }

    const [details, purchasesRes] = await Promise.all([this.loadCustDetails(), this.loadPtsPurchases()]);
    if (!this.loyaltyRefreshIsLatest(root, mySeq)) return;

    const pointsTally = this.customerPointsTally(details);
    this.syncPointsTally(pointsTally);
    const purchases = this.pointsPurchasesList(purchasesRes);
    const unusedPurchases = purchases.filter(purchase => purchase && purchase.used_at == null);
    const hasLowPtsGift = unusedPurchases.some(purchase => this.purchasePtsCost(purchase) < MIN_PTS);
    const canShow = hasLowPtsGift || (Number.isFinite(pointsTally) && pointsTally > MIN_PTS);

    if (!canShow) {
      if (this.loyaltyRefreshIsLatest(root, mySeq)) {
        this.setRootLoading(root, false);
        root.hidden = true;
      }
      return;
    }

    const claimedByProd = new Map();
    const claimedByRw = new Map();
    unusedPurchases.forEach(purchase => {
      const redeemVal = this.purchaseRedeemId(purchase);
      const prodId = this.purchaseProdId(purchase);
      if (prodId && redeemVal && !claimedByProd.has(prodId)) {
        claimedByProd.set(prodId, redeemVal);
      }
      if (redeemVal && !claimedByRw.has(redeemVal)) {
        claimedByRw.set(redeemVal, redeemVal);
      }
    });

    cards.forEach(card => {
      if (!(card instanceof HTMLElement)) return;
      const prodId = this.normProdId(card.dataset.productId || "");
      const catalogRwId = card.dataset.rewardId || "";
      const catalogRwNorm = this.normalizeRewardId(catalogRwId);
      const ptsNeed = Number(card.dataset.pointsCost || 0);
      const redeemBtn = card.querySelector("[data-loyalty-redeem]");
      const ptsDisp = card.querySelector("[data-loyalty-pts-display]");
      const badge = card.querySelector("[data-loyalty-claimed-badge]");

      const purchRwId = claimedByProd.get(prodId) || claimedByRw.get(catalogRwNorm) || "";
      const isClaimed = Boolean(purchRwId);

      if (isClaimed) {
        card.dataset.loyaltyCardState = "claimed";
        card.hidden = false;
        if (ptsDisp instanceof HTMLElement) {
          ptsDisp.textContent = "0 pts";
        }
        if (badge instanceof HTMLElement) {
          badge.hidden = false;
        }
        if (redeemBtn instanceof HTMLElement) {
          this.syncRedeemBtnLabels(redeemBtn, true);
          redeemBtn.dataset.rewardId = purchRwId || catalogRwId;
          const purchRwNorm = this.normalizeRewardId(purchRwId);
          const matchPur = this.findClaimedPurchase(unusedPurchases, prodId, purchRwNorm);
          const rwObj =
            matchPur?.reward && typeof matchPur.reward === "object"
              ? /** @type {Record<string, unknown>} */ (matchPur.reward)
              : null;
          const finalVid = this.pickVariantIdFromReward(rwObj);
          const finalCode = matchPur && typeof matchPur === "object" ? this.pickDiscountCodeFromPurchase(matchPur) : "";
          if (finalVid) redeemBtn.dataset.loyaltyVariantId = finalVid;
          else delete redeemBtn.dataset.loyaltyVariantId;
          if (finalCode) redeemBtn.dataset.loyaltyRewardCode = finalCode;
          else delete redeemBtn.dataset.loyaltyRewardCode;
        }
        return;
      }

      card.dataset.loyaltyCardState = "redeem";
      if (badge instanceof HTMLElement) {
        badge.hidden = true;
      }
      if (ptsDisp instanceof HTMLElement) {
        ptsDisp.textContent = `${Math.round(ptsNeed)} pts`;
      }
      if (!Number.isFinite(pointsTally) || pointsTally < ptsNeed) {
        card.hidden = true;
        return;
      }

      card.hidden = false;
      if (redeemBtn instanceof HTMLElement) {
        this.syncRedeemBtnLabels(redeemBtn, false);
        redeemBtn.dataset.rewardId = catalogRwId;
        delete redeemBtn.dataset.loyaltyVariantId;
        delete redeemBtn.dataset.loyaltyRewardCode;
      }
    });

    if ([...cards].every(card => card instanceof HTMLElement && card.hidden)) {
      if (this.loyaltyRefreshIsLatest(root, mySeq)) {
        this.setRootLoading(root, false);
        root.hidden = true;
      }
      return;
    }

    if (!this.loyaltyRefreshIsLatest(root, mySeq)) return;

    this.setRootLoading(root, false);
    if (track instanceof HTMLElement) {
      track.hidden = false;
    }
    root.hidden = false;
  }

  /**
   * Syncs points tally for compact loyalty UI when reward is already in cart.
   * @returns {Promise<void>}
   */
  async refreshPointsTally() {
    if (!this.isRivoReady() || typeof window.RivoJS === "undefined" || window.RivoJS == null) {
      this.syncPointsTally(NaN);
      return;
    }
    const details = await this.loadCustDetails();
    const pointsTally = this.customerPointsTally(details);
    this.syncPointsTally(pointsTally);
  }

  /**
   * @param {string} handle
   * @returns {Promise<string>}
   */
  async fetchOptionsSection(handle) {
    const rootPath = window.Shopify?.routes?.root || "/";
    const url = `${rootPath}products/${encodeURIComponent(handle)}?sections=loyalty-product-options`;
    const res = await fetch(url);
    if (!res.ok) throw new Error("Section fetch failed");
    const data = await res.json();
    const html = data["loyalty-product-options"];
    return typeof html === "string" ? html : "";
  }

  /**
   * @param {HTMLElement} host
   * @param {string} html
   */
  injectSectionHtml(host, html) {
    host.innerHTML = html;
    host.querySelectorAll("script").forEach(oldScript => {
      const newScript = document.createElement("script");
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    });
  }

  refreshAllLoyaltyRoots() {
    document.querySelectorAll("[data-loyalty-cart-root]").forEach(el => {
      if (el instanceof HTMLElement) void this.refreshLoyalty(el);
    });
    void this.refreshPointsTally();
  }

  /**
   * Rivo redeemReward expects a numeric reward id when using integer catalog ids.
   * RivoJS functions return a Promise per vendor docs.
   * @param {string | number} rewardIdRaw
   * @returns {Promise<Record<string, unknown> | null>}
   */
  async callRedeemReward(rewardIdRaw) {
    if (!this.isRivoReady() || typeof window.RivoJS?.redeemReward !== "function") return null;
    const str = this.toStr(rewardIdRaw).trim();
    const rewardNum = Number(str);
    const out =
      /^\d+$/.test(str) && Number.isFinite(rewardNum)
        ? window.RivoJS.redeemReward(rewardNum)
        : window.RivoJS.redeemReward(rewardIdRaw);
    const settled = await Promise.resolve(out);
    return settled && typeof settled === "object" ? /** @type {Record<string, unknown>} */ (settled) : null;
  }

  setupDelegation() {
    if (this.clickHooked) return;
    this.clickHooked = true;

    document.addEventListener("click", async event => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const redeemBtn = target.closest("[data-loyalty-redeem]");
      if (redeemBtn instanceof HTMLElement) {
        const root = redeemBtn.closest("[data-loyalty-cart-root]");
        if (!(root instanceof HTMLElement)) return;

        const rewardId = redeemBtn.dataset.rewardId || "";
        const handle = redeemBtn.dataset.productHandle || "";
        const isMulti = redeemBtn.dataset.multiOption === "true";
        if (!rewardId) return;

        const drawerHost = root.querySelector("[data-loyalty-option-drawer-body]");
        const dialog = root.querySelector("[data-loyalty-dialog]");

        if (isMulti && handle && drawerHost instanceof HTMLElement && dialog instanceof HTMLElement) {
          drawerHost.innerHTML = `<p class="text-14-regular text-asphalt px-4 py-8">Loading…</p>`;
          if (typeof dialog.showDialog === "function") {
            dialog.showDialog();
          }
          try {
            const html = await this.fetchOptionsSection(handle);
            this.injectSectionHtml(drawerHost, html);
            const confirmBtn = root.querySelector("[data-loyalty-drawer-redeem]");
            const cardEl = redeemBtn.closest("[data-loyalty-card]");
            if (confirmBtn instanceof HTMLElement) {
              confirmBtn.dataset.rewardId = rewardId;
              if (cardEl instanceof HTMLElement) {
                confirmBtn.dataset.loyaltyCardState = cardEl.dataset.loyaltyCardState || "redeem";
                confirmBtn.dataset.loyaltyProductId = cardEl.dataset.productId || "";
                confirmBtn.dataset.loyaltyRewardCode = redeemBtn.dataset.loyaltyRewardCode || "";
              }
            }
          } catch (error) {
            console.error("[Loyalty Cart] Failed to load loyalty options section", error);
            drawerHost.innerHTML = `<p class="text-14-regular text-asphalt px-4 py-6">Options unavailable. Try again.</p>`;
          }
          return;
        }

        const cardEl = redeemBtn.closest("[data-loyalty-card]");
        const cardState = cardEl instanceof HTMLElement ? cardEl.dataset.loyaltyCardState || "redeem" : "redeem";
        const prodIdNorm = this.normProdId(cardEl instanceof HTMLElement ? cardEl.dataset.productId || "" : "");

        if (cardState === "claimed" || this.isRivoReady()) {
          this.setCardRedeemBusy(redeemBtn, true);
          try {
            if (cardState === "claimed") {
              const variantId = redeemBtn.dataset.loyaltyVariantId || "";
              const discountCode = redeemBtn.dataset.loyaltyRewardCode || "";
              if (variantId) await this.loyaltyAddVariantWithDiscount(variantId, discountCode);
            } else {
              const redeemRes = await this.callRedeemReward(rewardId);
              const reward = this.extractRewardFromRedeemRes(redeemRes);
              if (reward && redeemRes) {
                const rwProdId = this.rewardProductIdFromReward(reward);
                if (rwProdId && rwProdId === prodIdNorm) {
                  const variantId = this.pickVariantIdFromReward(reward);
                  const code = this.pickCodeFromRedeemResByProdId(redeemRes, prodIdNorm);
                  if (variantId) await this.loyaltyAddVariantWithDiscount(variantId, code);
                }
              }
            }
          } catch (error) {
            console.error("[Loyalty Cart] Failed loyalty card redeem flow", error);
          }
          try {
            await this.refreshLoyalty(root);
          } catch (error) {
            console.error("[Loyalty Cart] Failed refreshing loyalty UI", error);
          }
          this.setCardRedeemBusy(redeemBtn, false);
        }
        return;
      }

      const drawerRedeem = target.closest("[data-loyalty-drawer-redeem]");
      if (drawerRedeem instanceof HTMLElement) {
        const root = drawerRedeem.closest("[data-loyalty-cart-root]");
        if (!(root instanceof HTMLElement)) return;

        const dialog = root.querySelector("[data-loyalty-dialog]");
        const rewardId = drawerRedeem.dataset.rewardId || "";
        const cardState = drawerRedeem.dataset.loyaltyCardState || "redeem";
        const prodId = this.normProdId(drawerRedeem.dataset.loyaltyProductId || "");
        const drawerHost = root.querySelector("[data-loyalty-option-drawer-body]");
        const variantId = drawerHost instanceof HTMLElement ? this.readDrawerVariantId(drawerHost) : "";
        const rewardCode = drawerRedeem.dataset.loyaltyRewardCode || "";

        if (!rewardId || !variantId) return;
        if (cardState !== "claimed" && !this.isRivoReady()) return;

        this.setDrawerRedeemBusy(drawerRedeem, true);
        try {
          if (cardState === "claimed") {
            await this.loyaltyAddVariantWithDiscount(variantId, rewardCode);
          } else {
            const redeemRes = await this.callRedeemReward(rewardId);
            const reward = this.extractRewardFromRedeemRes(redeemRes);
            if (reward && redeemRes) {
              const rwProdId = this.rewardProductIdFromReward(reward);
              if (rwProdId && rwProdId === prodId) {
                const code = this.pickCodeFromRedeemResByProdId(redeemRes, prodId);
                await this.loyaltyAddVariantWithDiscount(variantId, code);
              }
            }
          }
          await this.refreshLoyalty(root);
          if (dialog instanceof HTMLElement && typeof dialog.closeDialog === "function") {
            dialog.closeDialog();
          }
        } catch (error) {
          console.error("[Loyalty Cart] Failed loyalty drawer redeem flow", error);
        } finally {
          this.setDrawerRedeemBusy(drawerRedeem, false);
        }
      }
    });
  }

  /**
   * Re-run loyalty UI logic after the cart section (or any morphed section) updates the DOM.
   * Idempotent delegation; refreshes every `[data-loyalty-cart-root]` (e.g. block injected again after removing a loyalty discount line).
   */
  reinitializeLoyaltyCart() {
    this.setupDelegation();
    this.refreshAllLoyaltyRoots();
  }

  initialize() {
    this.setupDelegation();

    document.addEventListener(RIVO_LOAD_EV, () => {
      this.rivoLoadReceived = true;
      this.refreshAllLoyaltyRoots();
    });

    if (!this.sectionMorphHooked) {
      this.sectionMorphHooked = true;
      document.addEventListener(ThemeEvents.sectionMorph, () => {
        queueMicrotask(() => this.reinitializeLoyaltyCart());
      });
    }
  }
}

const loyaltyCart = new LoyaltyCart();

// Run at module evaluation: this script is omitted when a loyalty discount is in the cart, so do not
// defer to DOMContentLoaded (may have fired already, or the tag may disappear on the next morph).
loyaltyCart.initialize();
// Catch roots when this module loads after `section:morph` (e.g. loyalty block first injected on cart morph).
queueMicrotask(() => loyaltyCart.reinitializeLoyaltyCart());
